import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Flame, TrendingUp, BarChart3 } from 'lucide-react';
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, Cell,
    LineChart, Line, CartesianGrid, Legend
} from 'recharts';

export default function GodModeAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTeams, setSelectedTeams] = useState([]);

    useEffect(() => {
        API.get('/analytics/god-mode').then(res => {
            setData(res.data);
            setLoading(false);
            if (res.data?.teams?.length >= 2) {
                setSelectedTeams([res.data.teams[0].id, res.data.teams[1].id]);
            }
        }).catch(() => setLoading(false));
    }, []);

    if (loading || !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const team1 = data.teams.find(t => t.id === selectedTeams[0]);
    const team2 = data.teams.find(t => t.id === selectedTeams[1]);

    const radarData = team1 && team2 ? [
        { metric: 'Strength', a: team1.strength, b: team2.strength },
        { metric: 'Batting', a: team1.batting, b: team2.batting },
        { metric: 'Bowling', a: team1.bowling, b: team2.bowling },
        { metric: 'Win Rate', a: team1.win_rate, b: team2.win_rate },
        { metric: 'Impact', a: team1.avg_player_impact * 10, b: team2.avg_player_impact * 10 },
    ] : [];

    return (
        <div className="space-y-6" data-testid="god-mode-analytics">
            <div className="glass-card p-6">
                <div className="flex items-center gap-3">
                    <Flame className="w-5 h-5 text-orange-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">GOD MODE ANALYTICS</h2>
                    <div className="badge-ai">ADVANCED</div>
                </div>
            </div>

            {/* Power Rankings */}
            <div className="glass-card p-6">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-4 h-4 text-yellow-400" />
                    <h3 className="heading-primary text-sm text-white">POWER RANKINGS</h3>
                </div>
                <div className="space-y-2">
                    {data.teams.map((team, i) => (
                        <div key={team.id} data-testid={`power-rank-${team.id}`}
                            className={`flex items-center gap-4 p-3 rounded-lg transition-all cursor-pointer ${selectedTeams.includes(team.id) ? 'bg-white/10 border border-white/10' : 'bg-white/5 hover:bg-white/8'}`}
                            onClick={() => {
                                if (selectedTeams.includes(team.id)) return;
                                setSelectedTeams(prev => [prev[1], team.id]);
                            }}>
                            <span className={`heading-primary text-2xl w-8 ${i < 3 ? 'text-yellow-400' : 'text-white/30'}`}>{team.power_rank}</span>
                            <div className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-black" style={{ backgroundColor: `${team.color}20`, color: team.color }}>
                                {team.short_name?.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="heading-primary text-sm text-white">{team.short_name}</span>
                                    {team.titles > 0 && <span className="text-[10px] text-yellow-400 font-bold">{team.titles}x CHAMP</span>}
                                </div>
                                <div className="flex gap-4 mt-1">
                                    <span className="text-[10px] text-white/30">BAT: <span className="text-cyan-400">{team.batting}</span></span>
                                    <span className="text-[10px] text-white/30">BOWL: <span className="text-pink-400">{team.bowling}</span></span>
                                    <span className="text-[10px] text-white/30">NRR: <span className={team.nrr > 0 ? 'text-green-400' : 'text-red-400'}>{team.nrr > 0 ? '+' : ''}{team.nrr}</span></span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="heading-primary text-2xl" style={{ color: team.color }}>{team.strength}</div>
                                <div className="tech-mono text-[10px] text-white/30">STRENGTH</div>
                            </div>
                            <div className="w-24 h-2 rounded-full bg-white/5 overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${team.strength}%`, backgroundColor: team.color }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Radar Comparison */}
                <div className="lg:col-span-6 glass-card p-6">
                    <h3 className="heading-primary text-sm text-white mb-2">TEAM COMPARISON</h3>
                    <div className="tech-mono text-[10px] text-white/30 mb-4">
                        <span style={{ color: team1?.color }}>{team1?.short_name}</span> vs <span style={{ color: team2?.color }}>{team2?.short_name}</span>
                        <span className="ml-2 text-white/20">(Click teams above to compare)</span>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                            <PolarGrid stroke="#333" />
                            <PolarAngleAxis dataKey="metric" tick={{ fill: '#999', fontSize: 11 }} />
                            <PolarRadiusAxis hide />
                            <Radar dataKey="a" stroke={team1?.color || '#00E5FF'} fill={team1?.color || '#00E5FF'} fillOpacity={0.15} name={team1?.short_name} />
                            <Radar dataKey="b" stroke={team2?.color || '#FF2D95'} fill={team2?.color || '#FF2D95'} fillOpacity={0.15} name={team2?.short_name} />
                            <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} />
                            <Legend />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>

                {/* Head to Head */}
                <div className="lg:col-span-6 glass-card p-6">
                    <h3 className="heading-primary text-sm text-white mb-4">HEAD-TO-HEAD RECORDS</h3>
                    <div className="space-y-3 max-h-[340px] overflow-y-auto pr-2">
                        {data.head_to_head.map((h, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                                <span className="text-xs font-bold w-10 text-right" style={{ color: h.color_a }}>{h.team_a}</span>
                                <div className="flex-1 flex items-center gap-1">
                                    <div className="h-3 rounded-l-full" style={{ width: `${(h.a_wins / h.total) * 100}%`, backgroundColor: h.color_a, minWidth: '16px' }} />
                                    <div className="h-3 rounded-r-full" style={{ width: `${(h.b_wins / h.total) * 100}%`, backgroundColor: h.color_b, minWidth: '16px' }} />
                                </div>
                                <span className="text-xs font-bold w-10" style={{ color: h.color_b }}>{h.team_b}</span>
                                <span className="text-[10px] text-white/30 w-12 text-right">{h.a_wins}-{h.b_wins}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Season Trends */}
                <div className="lg:col-span-12 glass-card p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-4 h-4 text-green-400" />
                        <h3 className="heading-primary text-sm text-white">IPL SEASON TRENDS</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={data.season_trends}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                            <XAxis dataKey="season" tick={{ fill: '#666', fontSize: 10 }} />
                            <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                            <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                            <Legend />
                            <Line type="monotone" dataKey="avg_score" stroke="#00E5FF" strokeWidth={2} dot={{ fill: '#00E5FF', r: 4 }} name="Avg Score" />
                            <Line type="monotone" dataKey="sixes_per_match" stroke="#FF2D95" strokeWidth={2} dot={{ fill: '#FF2D95', r: 4 }} name="Sixes/Match" />
                            <Line type="monotone" dataKey="boundaries_per_match" stroke="#39FF14" strokeWidth={2} dot={{ fill: '#39FF14', r: 4 }} name="Boundaries/Match" />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
